/* eslint-disable no-unused-vars */
import React from "react";
import PropTypes from "prop-types";
import { Card, Box, Typography, IconButton, Tooltip } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import { Link } from "react-router-dom";

function ClientAnalyticsCard({ clientsCreated, enquiries, recentClients, percentageChange }) {
  const getTimeAgo = (date) => {
    const now = new Date();
    const createdDate = new Date(date);
    const diffTime = Math.abs(now - createdDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    if (diffDays === 2) return "2 days ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffMonths >= 1 && diffDays % 30 === 0)
      return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
    if (diffMonths >= 1) {
      const remainingDays = diffDays % 30;
      return remainingDays > 0
        ? `${diffMonths} month${diffMonths > 1 ? "s" : ""}, ${remainingDays} day${
            remainingDays > 1 ? "s" : ""
          } ago`
        : `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
    }
    if (diffWeeks === 1 && diffDays % 7 === 0) return "1 week ago";
    if (diffWeeks === 1) {
      const remainingDays = diffDays % 7;
      return remainingDays > 0
        ? `1 week, ${remainingDays} day${remainingDays > 1 ? "s" : ""} ago`
        : "1 week ago";
    }
    if (diffDays % 7 === 0) return `${diffWeeks} weeks ago`;
    const remainingDays = diffDays % 7;
    return `${diffWeeks} weeks, ${remainingDays} day${remainingDays > 1 ? "s" : ""} ago`;
  };

  const getAvatarColor = (index) => {
    const colors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DFE6E9"];
    return colors[index % colors.length];
  };

  return (
    <Card
      sx={{
        height: "100%",
        p: 2,
        boxShadow: "0px 3px 10px rgba(0,0,0,0.08)",
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <ArgonBox display="flex" alignItems="center" gap={1}>
          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
            Client Analytics
          </ArgonTypography>
          <Tooltip title="Client statistics for the past 28 days" arrow>
            <IconButton size="small" sx={{ p: 0 }}>
              <InfoIcon sx={{ fontSize: 18, color: "#666" }} />
            </IconButton>
          </Tooltip>
        </ArgonBox>
      </ArgonBox>

      {/* Stats Section */}
      <ArgonBox display="flex" gap={3} mb={2}>
        {/* Clients Created */}
        <ArgonBox flex={1}>
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            Clients Created
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary" display="block" mb={1}>
            Past 28 days
          </ArgonTypography>
          <ArgonTypography variant="h3" fontWeight="bold" color="dark" mb={0.5}>
            {clientsCreated}
          </ArgonTypography>
          <ArgonBox display="flex" alignItems="center" gap={0.5}>
            <ArgonTypography
              variant="button"
              fontWeight="bold"
              color="success"
              sx={{ display: "flex", alignItems: "center" }}
            >
              ↑ {percentageChange.clients}%
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text">
              vs last 28 days
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>

        {/* Enquiries */}
        <ArgonBox flex={1}>
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            Enquiries
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary" display="block" mb={1}>
            Past 28 days
          </ArgonTypography>
          <ArgonTypography variant="h3" fontWeight="bold" color="dark" mb={0.5}>
            {enquiries}
          </ArgonTypography>
          <ArgonBox display="flex" alignItems="center" gap={0.5}>
            <ArgonTypography
              variant="button"
              fontWeight="bold"
              color="success"
              sx={{ display: "flex", alignItems: "center" }}
            >
              ↑ {percentageChange.enquiries}%
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text">
              vs last 28 days
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>

      {/* Recently Created Section */}
      <ArgonBox>
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <ArgonTypography variant="button" fontWeight="medium" color="dark">
            Recently Created
          </ArgonTypography>
          <Link to="/parents" style={{ textDecoration: "none" }}>
            <ArgonTypography
              variant="caption"
              color="info"
              fontWeight="medium"
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View more clients
            </ArgonTypography>
          </Link>
        </ArgonBox>

        {/* Client List */}
        <ArgonBox>
          {recentClients && recentClients.length > 0 ? (
            recentClients.slice(0, 4).map((client, index) => (
              <ArgonBox
                key={client._id || client.id || index}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={1.5}
                sx={{
                  borderBottom: index < 3 ? "1px solid #f0f0f0" : "none",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5}>
                  <ArgonAvatar
                    src={client.profileImage}
                    alt={client.fullName}
                    size="sm"
                    variant="rounded"
                    sx={{
                      "& img": {
                        objectFit: "cover",
                        width: "100%",
                        height: "100%",
                      },
                    }}
                  />
                  <Link
                    to={`/parents/profile/${client._id || client.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <ArgonTypography
                      variant="button"
                      color="dark"
                      fontWeight="medium"
                      sx={{ textDecoration: "underline", textTransform: "none" }}
                    >
                      {client.fullName || "Unknown Client"}
                    </ArgonTypography>
                  </Link>
                </ArgonBox>
                <ArgonTypography variant="caption" color="text">
                  {getTimeAgo(client.createdAt)}
                </ArgonTypography>
              </ArgonBox>
            ))
          ) : (
            <ArgonTypography variant="caption" color="text" textAlign="center" display="block">
              No recent clients
            </ArgonTypography>
          )}
        </ArgonBox>
      </ArgonBox>
    </Card>
  );
}

ClientAnalyticsCard.defaultProps = {
  clientsCreated: 0,
  enquiries: 0,
  recentClients: [],
  percentageChange: {
    clients: 0,
    enquiries: 0,
  },
};

ClientAnalyticsCard.propTypes = {
  clientsCreated: PropTypes.number,
  enquiries: PropTypes.number,
  recentClients: PropTypes.array,
  percentageChange: PropTypes.shape({
    clients: PropTypes.number,
    enquiries: PropTypes.number,
  }),
};

export default ClientAnalyticsCard;
